<!-- ============================================================
  IdModal.vue — the ONE standard dialog (replaces every <v-dialog>).
  Pink-gradient head (icon + title + subtitle + × close), scrollable
  body slot, optional right-aligned footer slot. ESC / scrim-click close;
  on mobile it docks to the bottom as a sheet. All styling is global
  (.id-modal* in istar-design.css) — no scoped CSS here.

  Props:
    modelValue Boolean  v-model open state
    title      String   head title
    subtitle   String   head subtitle (optional)
    icon       String   mdi class e.g. 'mdi-account-plus' (optional)
    size       String   sm(≤430) | md(≤560, default) | lg(≤760) | xl(≤980)
    noFooter   Boolean  hide the footer slot
    persistent Boolean  don't close on scrim-click / ESC (for forms mid-edit)
    steadyHeight Boolean  ล็อกความสูง body ไม่ให้ยืด-หด — ใช้กับ dialog ที่มีแท็บ (ดูด้านล่าง)
  Slots: default (body), footer
  Emits: update:modelValue, close

  ── steadyHeight: มาตรฐาน "dialog ที่มีแท็บต้องไม่ขยับ" (เจ้าของสั่ง 2026-08-22) ───────────────
  ตอนเปิด dialog จะ **วัดทุกแท็บล่วงหน้าในจังหวะเดียว** แล้วล็อก min-height ของ body ไว้ที่แท็บที่
  สูงที่สุด ตั้งแต่เฟรมแรก — สลับแท็บไหนกล่องก็ไม่ขยับเลยแม้แต่ครั้งแรก
  (เวอร์ชันแรกใช้วิธี "โตแล้วค้าง" คือค่อยโตตอนเจอแท็บที่สูงกว่า เจ้าของบอกว่ามันเด้ง ดูตลก — เลิกใช้แล้ว)

  วิธีใช้ในหน้าที่มีแท็บ (ทำ 3 อย่าง):
    1. `<id-modal steady-height>`
    2. แต่ละแท็บเป็น element เดียวที่มี `class="id-tabpane"`
    3. สลับแท็บด้วย **`v-show` ไม่ใช่ `v-if`** — ต้องอยู่ใน DOM ครบทุกแท็บถึงจะวัดล่วงหน้าได้
       (และห้ามใส่ .id-fade-in ที่แพนแท็บ เพราะ element ไม่ได้ถูกสร้างใหม่ อนิเมชันจะเล่นแค่รอบแรก)
  วัดด้วยการโชว์ทีละแท็บแล้วอ่านความสูง ทั้งหมดในจังหวะซิงโครนัสเดียว เบราว์เซอร์จึงไม่ทันวาด
  = ผู้ใช้ไม่เห็นอะไรกระพริบ · ค่าที่ล็อกถูกตัดไม่ให้เกินพื้นที่จริงของจอ (max-height − หัว − ท้าย)
  ไม่งั้นปุ่มท้าย dialog จะถูกดันหลุดขอบจอ · วัดใหม่เมื่อเนื้อหาเปลี่ยน/จอเปลี่ยนขนาด · รีเซ็ตเมื่อปิด

  ถ้าไม่มี .id-tabpane เลย จะถอยไปใช้วิธีเดิม (จำความสูงสูงสุดที่เคยเห็น ไม่หด) ซึ่งพอใช้ได้กับ
  dialog ที่เนื้อหายืดหดเอง แต่ไม่ใช่กรณีแท็บ
============================================================ -->
<template>
  <teleport to="body">
    <transition name="modal-fade">
      <div v-if="modelValue" class="id-modal-scrim" ref="scrim" @mousedown.self="onScrim">
        <div class="id-modal" :class="size" role="dialog" aria-modal="true">
          <div class="id-modal-head">
            <div v-if="icon" class="id-modal-ico"><span class="mdi" :class="icon"></span></div>
            <div class="id-modal-titles">
              <div class="id-modal-title">{{ title }}</div>
              <div v-if="subtitle" class="id-modal-sub">{{ subtitle }}</div>
            </div>
            <button class="id-modal-x" @click="close" :aria-label="$t('common.close')"><span class="mdi mdi-close"></span></button>
          </div>
          <div class="id-modal-body" ref="body"
            :style="steadyHeight && bodyMinH ? { minHeight: bodyMinH + 'px' } : null"><slot></slot></div>
          <div v-if="!noFooter" class="id-modal-foot"><slot name="footer"></slot></div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: 'IdModal',
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: '' },
    subtitle: { type: String, default: '' },
    icon: { type: String, default: '' },
    size: { type: String, default: 'md' },
    noFooter: { type: Boolean, default: false },
    persistent: { type: Boolean, default: false },
    steadyHeight: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'close'],
  data() {
    return { bodyMinH: 0 };
  },
  methods: {
    close() { this.$emit('update:modelValue', false); this.$emit('close'); },
    onScrim() { if (!this.persistent) this.close(); },
    onKey(e) { if (e.key === 'Escape' && this.modelValue && !this.persistent) this.close(); },
    // พื้นที่แนวตั้งที่ body มีได้จริง = max-height ของกล่อง − หัว − ท้าย
    // ล็อกเกินค่านี้ไม่ได้ ไม่งั้น min-height จะดันจนแถบปุ่มท้าย dialog หลุดออกนอกจอ
    availableBodyH() {
      const el = this.$refs.body;
      const modal = el && el.parentElement;
      if (!modal) return Infinity;
      const maxH = parseFloat(getComputedStyle(modal).maxHeight);
      if (!maxH || Number.isNaN(maxH)) return Infinity;
      const head = modal.querySelector('.id-modal-head');
      const foot = modal.querySelector('.id-modal-foot');
      return Math.max(0, maxH - (head ? head.offsetHeight : 0) - (foot ? foot.offsetHeight : 0));
    },
    // วัดทุกแท็บในจังหวะเดียว: โชว์ทีละแพน อ่านความสูงรวมของ body แล้วคืนสภาพเดิม
    // ทั้งลูปอยู่ในงานซิงโครนัสเดียว เบราว์เซอร์ไม่วาดระหว่างนั้น ผู้ใช้จึงไม่เห็นแท็บอื่นแวบ
    measureAllPanes(panes) {
      const el = this.$refs.body;
      const shown = panes.map((p) => p.style.display);
      let tallest = 0;
      panes.forEach((_, i) => {
        panes.forEach((p, j) => { p.style.display = i === j ? '' : 'none'; });
        tallest = Math.max(tallest, el.scrollHeight);   // scrollHeight = ความสูงเนื้อหาจริง ไม่ติดเพดาน
      });
      panes.forEach((p, i) => { p.style.display = shown[i]; });
      return tallest;
    },
    // จุดเดียวที่คำนวณค่า min-height — เรียกได้ซ้ำ ๆ ผลลัพธ์เท่าเดิมถ้าเนื้อหาไม่เปลี่ยน
    lockHeight() {
      this.measureQueued = false;
      const el = this.$refs.body;
      if (!el) return;
      const panes = [...el.querySelectorAll('.id-tabpane')];
      if (!panes.length) {                 // ไม่ใช่ dialog แบบแท็บ — ถอยไปใช้ "สูงสุดที่เคยเห็น ไม่หด"
        if (el.clientHeight > this.bodyMinH) this.bodyMinH = el.clientHeight;
        return;
      }
      this.selfMutating = true;            // การโชว์/ซ่อนแพนของเราเอง ห้ามวนกลับมาวัดซ้ำ
      const tallest = this.measureAllPanes(panes);
      this.selfMutating = false;
      const next = Math.min(tallest, this.availableBodyH());
      if (next && next !== this.bodyMinH) this.bodyMinH = next;   // ลดลงได้ด้วย ถ้าเนื้อหาหายไปจริง
    },
    // รวบหลายเหตุการณ์ที่เกิดติด ๆ กันให้วัดครั้งเดียว — การอ่านความสูงบังคับให้เบราว์เซอร์คำนวณ layout
    scheduleLock() {
      if (this.selfMutating || this.measureQueued) return;
      this.measureQueued = true;
      setTimeout(this.lockHeight, 0);
    },
    // จอเปลี่ยนขนาด (ย่อหน้าต่าง / หมุนมือถือ / คีย์บอร์ดมือถือเด้ง) = เพดานพื้นที่เปลี่ยน
    // ต้องล้างค่าเดิมก่อนวัด ไม่งั้นค่าที่ล็อกไว้ตอนจอใหญ่จะค้างอยู่บนจอเล็ก
    resetAndRelock() {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.bodyMinH = 0;
        this.$nextTick(this.lockHeight);
      }, 150);
    },
    startWatchingBody() {
      if (!this.steadyHeight || this.bodyMo) return;
      this.$nextTick(() => {
        const el = this.$refs.body;
        if (!el) return;
        this.lockHeight();
        // ① เนื้อหาเปลี่ยน (แถวมาเพิ่ม / แท็บใหม่โผล่) — MutationObserver ทำงานทันทีที่ DOM เปลี่ยน
        //    ไม่ต้องรอเฟรม จึงเชื่อถือได้กว่า hook updated() ของ Vue ที่ไม่ยิงเมื่อพ่อแม่แพตช์สล็อตให้เอง
        this.bodyMo = new MutationObserver(this.scheduleLock);
        this.bodyMo.observe(el, { childList: true, subtree: true });
        // ② ขนาดเปลี่ยนโดย DOM ไม่เปลี่ยน (รูปโหลดเสร็จ / ฟอนต์มา / จอหมุน) — scrim คือ inset:0
        //    จึงมีขนาดเท่าจอเสมอ ใช้จับ "จอเปลี่ยนขนาด" ได้แม้ WebView ไม่ยิง event resize
        if (typeof ResizeObserver !== 'undefined') {
          this.bodyRo = new ResizeObserver(() => {
            const vh = this.$refs.scrim ? this.$refs.scrim.clientHeight : 0;
            if (this.lastScrimH && vh && vh !== this.lastScrimH) { this.lastScrimH = vh; this.resetAndRelock(); return; }
            this.lastScrimH = vh || this.lastScrimH;
            this.scheduleLock();
          });
          this.bodyRo.observe(el);
          if (this.$refs.scrim) { this.lastScrimH = this.$refs.scrim.clientHeight; this.bodyRo.observe(this.$refs.scrim); }
        }
        window.addEventListener('resize', this.resetAndRelock);
      });
    },
    stopWatchingBody() {
      if (this.bodyMo) { this.bodyMo.disconnect(); this.bodyMo = null; }
      if (this.bodyRo) { this.bodyRo.disconnect(); this.bodyRo = null; }
      window.removeEventListener('resize', this.resetAndRelock);
      clearTimeout(this.resizeTimer);
      this.measureQueued = false;
      this.selfMutating = false;
      this.lastScrimH = 0;
      this.bodyMinH = 0;   // ใบใหม่เริ่มวัดใหม่ ไม่งั้นความสูงของใบก่อนติดมาด้วย
    },
  },
  watch: {
    modelValue(v) {
      document.body.style.overflow = v ? 'hidden' : '';
      if (v) this.startWatchingBody(); else this.stopWatchingBody();
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKey);
    if (this.modelValue) this.startWatchingBody();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKey);
    document.body.style.overflow = '';
    this.stopWatchingBody();
  },
};
</script>
